import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  Home,
  ListChecks,
  Loader2,
  Mail,
  Plus,
  Save,
  Trash2,
  Wrench,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  CHECKLIST_KINDS,
  CHECKLIST_STATUSES,
  addChecklistItem,
  buildMailto,
  deleteChecklistItem,
  fetchChecklist,
  isNeedsAnswer,
  portalAnswersOf,
  sortChecklist,
  updateChecklistItem,
} from '@/lib/checklistData';

const KIND_ICONS = { email: Mail, portal: Globe, homeowner: Home, internal: Wrench };

const STATUS_STYLES = {
  todo: 'border-border text-muted-foreground',
  drafted: 'border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]',
  waiting: 'border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]',
  done: 'border-[hsl(var(--success))]/40 text-[hsl(var(--success))]',
  skipped: 'border-border text-muted-foreground line-through',
};

const EDIT_FIELDS = [
  'title',
  'kind',
  'status',
  'owner',
  'due_date',
  'waiting_on',
  'notes',
  'recipient_name',
  'recipient_email',
  'draft_subject',
  'draft_body',
  'portal_url',
];

const copyText = async (text, t) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(t('checklist.copied', { defaultValue: 'Copied' }));
    return true;
  } catch {
    toast.error(t('checklist.copy_failed', { defaultValue: 'Couldn’t copy — select and copy it by hand.' }));
    return false;
  }
};

const fmtDue = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * A pre-construction (or any) checklist for one estimate or job. PQ builds
 * and prepares items in chat; this is where the contractor works them:
 * check off, edit, open a drafted email in his mail app, copy portal answers
 * field by field. Nothing here sends or submits on its own.
 *
 * Pass refreshKey to reload (e.g. bump it when the PQ chat closes).
 */
export default function ChecklistPanel({ estimateId = null, jobId = null, refreshKey = 0, className }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, available: true, rows: [] });
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const recordId = estimateId || jobId;

  const load = useCallback(async () => {
    if (!recordId) return;
    try {
      const res = await fetchChecklist({ estimateId, jobId });
      setState({ loading: false, available: res.available, rows: res.rows });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      toast.error(e?.message || t('checklist.load_failed', { defaultValue: 'Couldn’t load the checklist.' }));
    }
  }, [estimateId, jobId, recordId, t]);

  useEffect(() => {
    setOpenId(null);
    setState({ loading: true, available: true, rows: [] });
  }, [recordId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const rows = useMemo(() => sortChecklist(state.rows), [state.rows]);
  const doneCount = rows.filter((r) => r.status === 'done' || r.status === 'skipped').length;

  const replaceRow = (row) =>
    setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === row.id ? row : r)) }));

  const toggleDone = async (row) => {
    const next = row.status === 'done' ? 'todo' : 'done';
    replaceRow({ ...row, status: next });
    try {
      replaceRow(await updateChecklistItem(row.id, { status: next }));
    } catch (e) {
      replaceRow(row);
      toast.error(e?.message || 'Update failed.');
    }
  };

  const openRow = (row) => {
    if (openId === row.id) {
      setOpenId(null);
      return;
    }
    setOpenId(row.id);
    setDraft(Object.fromEntries(EDIT_FIELDS.map((f) => [f, row[f] ?? ''])));
  };

  const saveRow = async (row) => {
    const patch = {};
    for (const f of EDIT_FIELDS) {
      const v = typeof draft[f] === 'string' ? draft[f].trim() : draft[f];
      const before = row[f] ?? '';
      if (v !== before) patch[f] = v === '' && f !== 'title' ? null : v;
    }
    if (!Object.keys(patch).length) {
      setOpenId(null);
      return;
    }
    if ('title' in patch && String(patch.title || '').length < 2) {
      toast.error(t('checklist.title_required', { defaultValue: 'Give the item a title.' }));
      return;
    }
    setSaving(true);
    try {
      replaceRow(await updateChecklistItem(row.id, patch));
      setOpenId(null);
    } catch (e) {
      toast.error(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (row) => {
    try {
      await deleteChecklistItem(row.id);
      setState((s) => ({ ...s, rows: s.rows.filter((r) => r.id !== row.id) }));
      setOpenId(null);
    } catch (e) {
      toast.error(e?.message || 'Delete failed.');
    }
  };

  const addRow = async () => {
    const title = newTitle.trim();
    if (title.length < 2) return;
    setAdding(true);
    try {
      const sort = rows.reduce((m, r) => Math.max(m, r.sort || 0), 0) + 1;
      const row = await addChecklistItem({ estimateId, jobId, title, sort });
      setState((s) => ({ ...s, rows: [...s.rows, row] }));
      setNewTitle('');
    } catch (e) {
      toast.error(e?.message || 'Add failed.');
    } finally {
      setAdding(false);
    }
  };

  const openInEmail = async () => {
    const { href, bodyInLink } = buildMailto({
      to: draft.recipient_email,
      subject: draft.draft_subject,
      body: draft.draft_body,
    });
    if (!bodyInLink) {
      const ok = await copyText(draft.draft_body, t);
      if (ok) {
        toast.info(
          t('checklist.body_copied_paste', {
            defaultValue: 'The message is long — it’s on your clipboard. Paste it into the email.',
          })
        );
      }
    }
    window.location.href = href;
    // Opening the draft isn't sending it — he marks it "waiting" once he's
    // actually hit Send, so nothing claims an email went out that didn't.
  };

  if (!recordId || !state.available) return null;

  return (
    <Card className={cn('border-[hsl(var(--hairline))] bg-[hsl(var(--card))]', className)} data-testid="checklist-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ListChecks size={16} className="text-[hsl(var(--primary))]" />
            <span className="text-xs uppercase tracking-[0.16em] font-semibold text-foreground">
              {t('checklist.title', { defaultValue: 'Checklist' })}
            </span>
            {rows.length ? (
              <span className="text-xs text-muted-foreground" data-testid="checklist-progress">
                {t('checklist.progress', {
                  defaultValue: '{{done}} of {{total}} done',
                  done: doneCount,
                  total: rows.length,
                })}
              </span>
            ) : null}
          </div>
          {state.loading ? <Loader2 size={14} className="animate-spin text-muted-foreground" /> : null}
        </div>

        {!state.loading && !rows.length ? (
          <p className="text-xs text-muted-foreground" data-testid="checklist-empty">
            {t('checklist.empty', {
              defaultValue:
                'No checklist yet. Ask PQ “Make a pre-con checklist for this job” — or add items below.',
            })}
          </p>
        ) : null}

        <ul className="space-y-1.5">
          {rows.map((row) => {
            const Icon = KIND_ICONS[row.kind] || Wrench;
            const isOpen = openId === row.id;
            const closed = row.status === 'done' || row.status === 'skipped';
            const answers = portalAnswersOf(row);
            const needs = answers.filter(isNeedsAnswer).length;
            return (
              <li
                key={row.id}
                className={cn('rounded-md border border-border', isOpen && 'border-[hsl(var(--primary))]/40')}
                data-testid={`checklist-item-${row.id}`}
              >
                <div className="flex items-start gap-2 p-2">
                  <Checkbox
                    checked={row.status === 'done'}
                    onCheckedChange={() => toggleDone(row)}
                    className="mt-0.5"
                    aria-label={t('checklist.mark_done', { defaultValue: 'Mark done' })}
                    data-testid="checklist-item-done"
                  />
                  <button
                    type="button"
                    onClick={() => openRow(row)}
                    className="flex-1 min-w-0 text-left"
                    data-testid="checklist-item-toggle"
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon size={13} className="shrink-0 text-muted-foreground" />
                      <span className={cn('text-sm text-foreground', closed && 'line-through text-muted-foreground')}>
                        {row.title}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                      <span className={cn('rounded border px-1 uppercase tracking-wider text-[10px]', STATUS_STYLES[row.status])}>
                        {t(`checklist.status_${row.status}`, { defaultValue: row.status })}
                      </span>
                      {row.owner ? <span>{row.owner}</span> : null}
                      {row.due_date ? (
                        <span>{t('checklist.due', { defaultValue: 'due {{date}}', date: fmtDue(row.due_date) })}</span>
                      ) : null}
                      {row.waiting_on ? (
                        <span>{t('checklist.waiting_on_short', { defaultValue: 'waiting on {{who}}', who: row.waiting_on })}</span>
                      ) : null}
                      {needs ? (
                        <span className="text-[hsl(var(--warning))]">
                          {t('checklist.needs_count', { count: needs })}
                        </span>
                      ) : null}
                    </div>
                  </button>
                  {isOpen ? (
                    <ChevronDown size={14} className="mt-1 text-muted-foreground" />
                  ) : (
                    <ChevronRight size={14} className="mt-1 text-muted-foreground" />
                  )}
                </div>

                {isOpen ? (
                  <div className="border-t border-border p-3 space-y-3" data-testid="checklist-item-editor">
                    <Input
                      value={draft.title}
                      onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                      maxLength={200}
                      className="h-9 bg-[hsl(var(--muted))] border-border"
                      data-testid="checklist-edit-title"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={draft.kind} onValueChange={(v) => setDraft((d) => ({ ...d, kind: v }))}>
                        <SelectTrigger className="h-9 bg-[hsl(var(--muted))] border-border text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CHECKLIST_KINDS.map((k) => (
                            <SelectItem key={k} value={k}>
                              {t(`checklist.kind_${k}`, { defaultValue: k })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
                        <SelectTrigger className="h-9 bg-[hsl(var(--muted))] border-border text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CHECKLIST_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {t(`checklist.status_${s}`, { defaultValue: s })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={draft.owner}
                        onChange={(e) => setDraft((d) => ({ ...d, owner: e.target.value }))}
                        placeholder={t('checklist.owner', { defaultValue: 'Who’s on it' })}
                        maxLength={120}
                        className="h-9 bg-[hsl(var(--muted))] border-border text-xs"
                      />
                      <Input
                        type="date"
                        value={draft.due_date || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, due_date: e.target.value }))}
                        className="h-9 bg-[hsl(var(--muted))] border-border text-xs"
                      />
                    </div>
                    <Input
                      value={draft.waiting_on}
                      onChange={(e) => setDraft((d) => ({ ...d, waiting_on: e.target.value }))}
                      placeholder={t('checklist.waiting_on', { defaultValue: 'Waiting on (who / what)' })}
                      maxLength={300}
                      className="h-9 bg-[hsl(var(--muted))] border-border text-xs"
                    />

                    {draft.kind === 'email' ? (
                      <div className="space-y-2 rounded-md bg-[hsl(var(--muted))]/50 p-2" data-testid="checklist-email">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={draft.recipient_name}
                            onChange={(e) => setDraft((d) => ({ ...d, recipient_name: e.target.value }))}
                            placeholder={t('checklist.to_name', { defaultValue: 'To (name)' })}
                            maxLength={120}
                            className="h-9 bg-[hsl(var(--card))] border-border text-xs"
                          />
                          <Input
                            type="email"
                            value={draft.recipient_email}
                            onChange={(e) => setDraft((d) => ({ ...d, recipient_email: e.target.value }))}
                            placeholder={t('checklist.to_email', { defaultValue: 'To (email)' })}
                            maxLength={200}
                            className="h-9 bg-[hsl(var(--card))] border-border text-xs"
                          />
                        </div>
                        <Input
                          value={draft.draft_subject}
                          onChange={(e) => setDraft((d) => ({ ...d, draft_subject: e.target.value }))}
                          placeholder={t('checklist.subject', { defaultValue: 'Subject' })}
                          maxLength={300}
                          className="h-9 bg-[hsl(var(--card))] border-border text-xs"
                        />
                        <Textarea
                          value={draft.draft_body}
                          onChange={(e) => setDraft((d) => ({ ...d, draft_body: e.target.value }))}
                          placeholder={t('checklist.body', { defaultValue: 'Message' })}
                          maxLength={8000}
                          rows={7}
                          className="bg-[hsl(var(--card))] border-border text-xs"
                          data-testid="checklist-email-body"
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyText(draft.draft_body || '', t)}
                            disabled={!draft.draft_body}
                            className="h-8 text-xs"
                          >
                            <Copy size={12} className="mr-1.5" />
                            {t('checklist.copy_message', { defaultValue: 'Copy message' })}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={openInEmail}
                            disabled={!draft.draft_body && !draft.draft_subject}
                            className="h-8 text-xs border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]"
                            data-testid="checklist-open-email"
                          >
                            <Mail size={12} className="mr-1.5" />
                            {t('checklist.open_email', { defaultValue: 'Open in email' })}
                          </Button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {t('checklist.email_hint', {
                            defaultValue:
                              'Opens a draft in your mail app — you hit Send. Then set this to “Waiting”.',
                          })}
                        </p>
                      </div>
                    ) : null}

                    {draft.kind === 'portal' || answers.length || row.portal_url ? (
                      <div className="space-y-2 rounded-md bg-[hsl(var(--muted))]/50 p-2" data-testid="checklist-portal">
                        <div className="flex items-center gap-2">
                          <Input
                            value={draft.portal_url}
                            onChange={(e) => setDraft((d) => ({ ...d, portal_url: e.target.value }))}
                            placeholder={t('checklist.portal_url', { defaultValue: 'Portal link (https://…)' })}
                            maxLength={500}
                            className="h-9 bg-[hsl(var(--card))] border-border text-xs"
                          />
                          {/^https?:\/\//i.test(draft.portal_url || '') ? (
                            <Button asChild size="sm" variant="outline" className="h-9 text-xs shrink-0">
                              <a href={draft.portal_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink size={12} className="mr-1.5" />
                                {t('checklist.open_portal', { defaultValue: 'Open' })}
                              </a>
                            </Button>
                          ) : null}
                        </div>
                        {answers.length ? (
                          <ul className="space-y-1" data-testid="checklist-portal-answers">
                            {answers.map((a, i) => {
                              const missing = isNeedsAnswer(a);
                              return (
                                <li
                                  key={`${a.field}-${i}`}
                                  className={cn(
                                    'flex items-start gap-2 rounded border px-2 py-1.5 text-xs',
                                    missing
                                      ? 'border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5'
                                      : 'border-border bg-[hsl(var(--card))]'
                                  )}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.field}</div>
                                    {missing ? (
                                      <div className="text-[hsl(var(--warning))]">{a.source || 'needs: —'}</div>
                                    ) : (
                                      <>
                                        <div className="text-foreground break-words">{a.value}</div>
                                        {a.source ? (
                                          <div className="text-[10px] text-muted-foreground">
                                            {t('checklist.from_source', { defaultValue: 'from {{source}}', source: a.source })}
                                          </div>
                                        ) : null}
                                      </>
                                    )}
                                  </div>
                                  {!missing ? (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => copyText(a.value, t)}
                                      className="h-7 w-7 shrink-0"
                                      aria-label={t('checklist.copy_field', { defaultValue: 'Copy {{field}}', field: a.field })}
                                    >
                                      <Copy size={12} />
                                    </Button>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">
                            {t('checklist.portal_empty', {
                              defaultValue: 'Ask PQ to prepare the answers this form needs.',
                            })}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground">
                          {t('checklist.portal_hint', {
                            defaultValue: 'You submit the form yourself — copy each answer across.',
                          })}
                        </p>
                      </div>
                    ) : null}

                    <Textarea
                      value={draft.notes}
                      onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                      placeholder={t('checklist.notes', { defaultValue: 'Notes' })}
                      maxLength={2000}
                      rows={2}
                      className="bg-[hsl(var(--muted))] border-border text-xs"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeRow(row)}
                        className="h-8 text-xs text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                        data-testid="checklist-item-delete"
                      >
                        <Trash2 size={12} className="mr-1.5" />
                        {t('actions.delete', { defaultValue: 'Delete' })}
                      </Button>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setOpenId(null)} className="h-8 text-xs">
                          {t('actions.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveRow(row)}
                          disabled={saving}
                          className="h-8 text-xs"
                          data-testid="checklist-item-save"
                        >
                          {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Save size={12} className="mr-1.5" />}
                          {t('actions.save', { defaultValue: 'Save' })}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            addRow();
          }}
        >
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t('checklist.add_placeholder', { defaultValue: 'Add an item…' })}
            maxLength={200}
            className="h-9 bg-[hsl(var(--muted))] border-border text-sm"
            data-testid="checklist-add-input"
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            disabled={adding || newTitle.trim().length < 2}
            className="h-9 shrink-0"
            data-testid="checklist-add-button"
          >
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
